import { Component } from "react"
import PropTypes from "prop-types"

import isEmpty from "lodash/isEmpty"

import ConnectButton from "./connect_button"

import ConnectedApplet from "./connected_applet"
import DisconnectedApplet from "./disconnected_applet"
import GrantAccess from "./grant_access"

import ConfigurationDescriptions from "./configuration_descriptions"
import Description from "./description"

import { reloadSensitiveDataAfterUpgrade } from "./api"

import UpgradeToProModal from "app/components/upgrade_to_pro_modal"
import UpgradePlanModal from "app/components/upgrade_plan_modal"
import CheckoutModal from "app/components/upgrade_to_pro_modal/checkout_modal.tsx"
import AppletFeedback from "app/components/platform_feedback/applet_feedback"
import { shouldUseAlternateBrandColor } from "shared/lib/alternate_brand_color"

import RubberBandPinchColor from "app/scripts/rubber_band_pinch_color"

import MobileAlert from "./mobile_alert"

import { connect } from "react-redux"
import { subscribe } from "redux-subscriber"

import { grantAccess, jumpToNextStep, requirementsMet } from "./reducers/connections"

import { loginUser } from "./reducers/user"

import debounce from "lodash/debounce"

import { disableApplet, updateApplet } from "./reducers/applet"

import {
  updateNextToBeConnected,
  connectClicked,
  clearLocalStorage,
  updateAnimationCollection,
  appletCreated,
  appletDisabled,
  appletEnabled,
  updateAppletConfigurations,
  updateAppletEnablementRules,
  logEvent,
  setUser,
  upgradeUserSubscription,
  requestUserAuth,
} from "./actions"

import TopLevelConfig from "./top_level_config"
import ArchiveDelete from "./archive_delete"
import AppletRecommendations from "./applet_recommendations"
import AppletDetails from "./applet_details"

export const ConnectionDetails = ({ applet, editPath, onConfigurationSwitchChange, appletDetails }) => (
  <>
    <ConfigurationDescriptions
      applet={applet}
      editPath={editPath}
      onConfigurationSwitchChange={onConfigurationSwitchChange}
    />
    {applet.description?.length > 0 ? <Description text={applet.description} /> : null}
    <AppletDetails details={appletDetails} />
  </>
)

ConnectionDetails.propTypes = {
  applet: PropTypes.object.isRequired,
  editPath: PropTypes.string,
  onConfigurationSwitchChange: PropTypes.func,
  appletDetails: PropTypes.object,
}

export class Connection extends Component {
  knobBgColorForDarkBrands = false

  constructor(props) {
    super(props)

    this.state = {
      pushEnabled: props.applet.push_enabled,
      upgradeToPro: { showModal: false },
      showingCheckoutModal: false,
      checkoutPlanTarget: "intermediate_pro",
    }

    // change the knob color when the applet has a dark color
    if (shouldUseAlternateBrandColor(props.applet.brand_color)) {
      this.knobBgColorForDarkBrands = true
    }

    this.calculateOnResize = debounce(() => {
      this.setState({
        connectButtonMaxFontSize: this.calculateConnectButtonFontSize(),
      })
    }, 100)
  }

  componentDidMount() {
    // update state in case this applet was saved elsewhere
    // the enable state always comes from the applet payload
    if (this.props.applet.status === "enabled_for_user") {
      this.props.setAsCreated(true)
      this.props.setAsEnabled(true)
      this.props.setAsDisabled(false)
    }

    const unsubAppletEnabled = subscribe("applet.enabled", state => {
      if (state.applet.enabled) {
        unsubAppletEnabled()
        this.props.clearSavedState()

        // communicate to everything on the page the applet has been enabled
        window.dispatchEvent(new CustomEvent("appletEnabled", { detail: { enabled: true } }))
      }
    })

    // take user to log in page if not logged in
    subscribe("connections.connectClicked", state => {
      !state.user.signedIn && this.props.requestLogin(state.user.authRequestedAt)
    })

    const unsubConnectClick = subscribe("connections.connectClicked", state => {
      // fire the event only when click to enable
      if (state.connections.connectClicked) {
        unsubConnectClick()

        const needsGrant = this.props.services.filter(p => {
          return !p.connected
        })

        if (isEmpty(needsGrant)) {
          this.props.logAction({
            eventName: "statechange",
            eventData: {
              object_type: "connection",
              object_id: this.props.applet.id,
              location_type: "connection",
              state: "services_checked",
            },
          })
        }
      }
    })

    // force that no static applets will be displayed with a connections path
    if (!this.props.programmable && window.location.href.includes("connections")) {
      window.history.pushState({}, "", `/applets/${this.props.applet.friendly_id}`)
    }

    // make the rubberband area the same color as the connection card header
    RubberBandPinchColor({ checkPath: true })

    window.addEventListener("resize", this.calculateOnResize)
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.calculateOnResize)
  }

  calculateConnectButtonFontSize = () => {
    if (document.body.clientWidth < 724) {
      // phone
      return 22
    } else if (document.body.clientWidth > 1444) {
      // large desktop
      return 30
    } else {
      // small desktops and tablets
      return 28
    }
  }

  state = {
    connectButtonMaxFontSize: this.calculateConnectButtonFontSize(),
  }

  executeConnectionFlow = () => {
    // don't jump to a step if the flow is finalized in the system of record
    const finalStates = ["disabled_for_user", "enabled_for_user"]
    !finalStates.includes(this.props.applet.status) && this.props.connectClicked && this.props.jumpToNextStep()
  }

  render() {
    const {
      connectClicked,
      disconnectClicked,
      showFeedbackForm,
      showMobileAlert,
      showPublishCta,
      urls,
      applet,
      user,
      sessionId,
      proPlusProductId,
    } = this.props
    const { status, appletDetails } = applet

    const userWithinAllowableTier = !appletDetails.normalized_applet.enablement_rules.block_user
    const userOwnsApplet = applet.author === user.login
    const userAtAppletLimit =
      status === "never_enabled_for_user" &&
      !userOwnsApplet &&
      !user.unlimitedAppletEnables &&
      user.appletQuotaSlotsRemaining === 0
    const allowUserToConnect = !userAtAppletLimit && userWithinAllowableTier
    const checkoutStep = userAtAppletLimit ? "applet_quota" : "applet_paid_features"
    const tierRequirement = userAtAppletLimit
      ? user.tier === "free"
        ? "intermediate_pro"
        : "pro"
      : appletDetails.normalized_applet.enablement_rules.minimum_tier

    const showProPrompt = () =>
      this.setState({
        upgradeToPro: { showModal: true },
      })

    const reloadDataAndConnect = appletStatus => {
      reloadSensitiveDataAfterUpgrade(applet.id).then(data => {
        this.props.updateAppletEnablementRules(data.applet.normalized_applet.enablement_rules)
        this.props.upgradeUserSubscription({
          ...this.props.user,
          ...data.me,
        })

        appletStatus === "disabled_for_user" ? this.props.reConnectApplet() : this.props.onConnectClick()
      })
    }

    return (
      <>
        {status === "enabled_for_user" ? (
          <>
            <ConnectedApplet
              mainService={this.props.mainService}
              connectClicked={connectClicked}
              programmable={this.props.programmable}
              onDisconnectClick={this.props.disableApplet}
              appletConnected={this.props.appletConnected}
              knobBgColorForDarkBrands={this.knobBgColorForDarkBrands}
              connectButtonMaxFontSize={this.state.connectButtonMaxFontSize}
            />

            <MobileAlert
              showMobileAlert={showMobileAlert}
              user={this.props.user}
              userDevice={this.props.userDevice}
              applet={this.props.applet}
              appStorePath={this.props.appStorePath}
              googlePlayStorePath={this.props.googlePlayStorePath}
            />
          </>
        ) : status === "disabled_for_user" ? (
          <DisconnectedApplet
            mainService={this.props.mainService}
            connectClicked={connectClicked}
            disconnectClicked={disconnectClicked}
            services={this.props.services}
            applet={this.props.applet}
            programmable={this.props.programmable}
            knobBgColorForDarkBrands={this.knobBgColorForDarkBrands}
            onConnectClick={e => (allowUserToConnect ? this.props.reConnectApplet(e) : showProPrompt())}
            appletConnected={this.props.appletConnected}
            animation={this.props.animation}
            updateAnimationCol={this.props.updateAnimationCol}
            connectButtonMaxFontSize={this.state.connectButtonMaxFontSize}
          />
        ) : connectClicked && allowUserToConnect ? (
          <GrantAccess
            programmable={this.props.programmable}
            mainService={this.props.nextServiceToBeConnected}
            services={this.props.services}
            onAccessGranted={this.props.onAccessGranted}
            appletConnected={this.props.appletConnected}
            lastServiceConnected={this.props.lastServiceConnected}
            animation={this.props.animation}
            updateAnimationCol={this.props.updateAnimationCol}
            requirementsMet={this.props.requirementsMet}
            appletIsSaving={this.props.appletIsSaving}
            executeConnectionFlow={this.executeConnectionFlow}
            connectButtonMaxFontSize={this.state.connectButtonMaxFontSize}
          />
        ) : (
          <ConnectButton
            mainService={this.props.mainService}
            connected={this.props.applet.status === "enabled_for_user"}
            onConnectClick={e =>
              !this.props.user.signedIn || allowUserToConnect ? this.props.onConnectClick(e) : showProPrompt()
            }
            programmable={this.props.programmable}
            knobBgColorForDarkBrands={this.knobBgColorForDarkBrands}
            connectButtonMaxFontSize={this.state.connectButtonMaxFontSize}
            playEnableAnimation
          />
        )}

        {user.id &&
          !allowUserToConnect &&
          (window.App.newCheckoutModal ? (
            <UpgradePlanModal
              onClose={() => this.setState({ upgradeToPro: { showModal: false } })}
              onUpgrade={() => reloadDataAndConnect(status)}
              proPlusProductId={proPlusProductId}
              step={checkoutStep}
              tierRequirement={tierRequirement}
              trackLocationType="connection"
              urls={urls}
              user={{
                eligibleForTrial: this.props.user.eligibleForTrial,
                isTrialing: this.props.user.isTrialing,
                subscriptionPaymentType: this.props.user.subscriptionPaymentType,
                tier: this.props.user.tier,
              }}
              visible={this.state.upgradeToPro.showModal}
            />
          ) : (
            <>
              <UpgradeToProModal
                visible={this.state.upgradeToPro.showModal}
                step={checkoutStep}
                tierRequirement={tierRequirement}
                onUpgrade={() => reloadDataAndConnect(status)}
                showCheckoutModal={target => {
                  this.setState({
                    checkoutPlanTarget: target,
                    showingCheckoutModal: true,
                  })
                }}
                onClose={() => this.setState({ upgradeToPro: { showModal: false } })}
                appletsInUse={this.props.user.appletQuotaSlotsTotal - this.props.user.appletQuotaSlotsRemaining}
                appletQuotaTotal={this.props.user.appletQuotaSlotsTotal}
                eligibleForTrial={this.props.user.eligibleForTrial}
                currentTier={this.props.user.tier}
                subscriptionPaymentType={this.props.user.subscriptionPaymentType}
                proPlusProductId={proPlusProductId}
                isTrialing={this.props.user.isTrialing}
                trackLocationType="connection"
              />
              <CheckoutModal
                visible={this.state.showingCheckoutModal}
                target={this.state.checkoutPlanTarget}
                onClose={() => this.setState({ showingCheckoutModal: false })}
                onUpgrade={() => reloadDataAndConnect(status)}
                locationType="connection"
                newProSubscriptionUrl={urls.newProSubscriptionUrl}
                newProPlusSubscriptionUrl={urls.newProPlusSubscriptionUrl}
                modalSubscriptionUrl={urls.modalSubscriptionUrl}
              />
            </>
          ))}

        <ConnectionDetails
          applet={this.props.applet}
          editPath={this.props.editPath}
          onConfigurationSwitchChange={this.props.updateAppletConfigurations}
          appletDetails={appletDetails}
        />
        {this.props.applet.status && (
          <TopLevelConfig applet={applet} user={user} urls={urls} showPublishCta={showPublishCta} />
        )}

        {showFeedbackForm ? (
          <AppletFeedback appletId={this.props.applet.id} serviceName={this.props.mainService.name} />
        ) : (
          <></>
        )}

        <ArchiveDelete applet={applet} user={user} myAppletURL={urls.myAppletURL} sessionId={sessionId} />

        <p className="txt-caption connection-id">
          ID <span>{this.props.applet.id}</span>
        </p>

        <AppletRecommendations applet={applet} />

        <div className="breath-space" />
      </>
    )
  }
}

const mapStateToProps = state => {
  const { config, applet, user, connections, error, animation } = state
  const services = Object.values(connections.permissions)

  return {
    services,
    user,
    applet: applet.applet,
    mainService: connections.mainService,
    nextServiceToBeConnected: connections.permissions[connections.nextToBeConnected] || connections.mainService,
    connectClicked: connections.connectClicked,
    disconnectClicked: connections.disconnectClicked,
    appletConnected: applet.enabled,
    returnTo: config.returnTo,
    appStorePath: config.appStorePath,
    googlePlayStorePath: config.googlePlayStorePath,
    automation: config.automation,
    needsConfig: config.needsConfig,
    editPath: config.editPath,
    userDevice: config.userDevice,
    error,
    lastServiceConnected: connections.lastServiceConnected,
    programmable: config.programmable,
    animation: animation.collection,
    appletIsSaving: applet.appletIsSaving,
    showFeedbackForm: config.showFeedbackForm,
    showMobileAlert: config.showMobileAlert,
    showPublishCta: config.showPublishCta,
    myAppletURL: config.myAppletURL,
  }
}

const mapDispatchToProps = dispatch => {
  return {
    // reflect the applet status, noop but for updating the local state
    setAsCreated: status => dispatch(appletCreated(status)),
    setAsDisabled: status => dispatch(appletDisabled(status)),
    setAsEnabled: status => dispatch(appletEnabled(status)),

    // call the server
    disableApplet: () => dispatch(disableApplet()),
    reConnectApplet: () => dispatch(updateApplet()),

    // remeber, everthing is async so this is your friend if you have to find
    // if all requirements are met and you're go to connect the applet
    requirementsMet: () => dispatch(requirementsMet()),

    nextToBeConnected: service => dispatch(updateNextToBeConnected(service)),
    onConnectClick: () => dispatch(connectClicked(true)),
    onAccessGranted: service => dispatch(grantAccess(service)),
    jumpToNextStep: () => dispatch(jumpToNextStep()),
    requestLogin: authRequestedAt => {
      authRequestedAt || dispatch(requestUserAuth())
      dispatch(loginUser())
    },
    logAction: event => dispatch(logEvent(event)),
    clearSavedState: () => dispatch(clearLocalStorage()),
    updateUser: user => dispatch(setUser(user)),
    upgradeUserSubscription: user => dispatch(upgradeUserSubscription(user)),
    updateAnimationCol: state => dispatch(updateAnimationCollection(state)),
    updateAppletConfigurations: state => dispatch(updateAppletConfigurations(state)),
    updateAppletEnablementRules: state => dispatch(updateAppletEnablementRules(state)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Connection)

Connection.propTypes = {
  ...TopLevelConfig.propTypes,
  ...MobileAlert.propTypes,
  applet: PropTypes.object.isRequired,
  services: PropTypes.array,
  mainService: PropTypes.object,
  nextServiceToBeConnected: PropTypes.object,
  programmable: PropTypes.bool,
  appletConnected: PropTypes.bool,
  jumpToNextStep: PropTypes.func,
  onConnectClick: PropTypes.func,
  connectClicked: PropTypes.bool,
  disconnectClicked: PropTypes.bool,
  onAccessGranted: PropTypes.func,
  nextToBeConnected: PropTypes.func,
  returnTo: PropTypes.string,
  requestLogin: PropTypes.func,
  logAction: PropTypes.func,
  updateAppletConfigurations: PropTypes.func,
  reConnectApplet: PropTypes.func,
  error: PropTypes.object,
  automation: PropTypes.bool,
  needsConfig: PropTypes.bool,
  editPath: PropTypes.string,
  lastServiceConnected: PropTypes.object,
  setAsDisabled: PropTypes.func,
  disableApplet: PropTypes.func,
  animation: PropTypes.object,
  updateAnimationCol: PropTypes.func,
  setAsCreated: PropTypes.func,
  setAsEnabled: PropTypes.func,
  requirementsMet: PropTypes.func,
  clearSavedState: PropTypes.func,
  updateUser: PropTypes.func,
  appletIsSaving: PropTypes.bool,
  showFeedbackForm: PropTypes.bool,
  showMobileAlert: PropTypes.bool,
}
